import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowClockwiseIcon } from "@phosphor-icons/react";

const METHOD_COLOR = { POST: "text-emerald-600", PATCH: "text-amber-600", PUT: "text-amber-600", DELETE: "text-[#FF2400]" };

export default function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [userEmail, setUserEmail] = useState("");
  const [method, setMethod] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    p.set("limit", "300");
    if (userEmail) p.set("user_email", userEmail);
    if (method !== "all") p.set("method", method);
    if (fromDate) p.set("from_date", fromDate);
    if (toDate) p.set("to_date", toDate);
    try {
      const r = await api.get(`/audit-logs?${p.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { toast.error("Failed to load audit log"); }
  }, [userEmail, method, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8" data-testid="audit-log-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-slate-500">Compliance</div>
          <h1 className="font-display text-4xl font-black">Audit Log</h1>
          <p className="text-slate-500 mt-2">Every state-changing action, with the user, IP, timestamp and endpoint hit.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="overline">User email</Label>
            <Input value={userEmail} onChange={(e) => setUserEmail(e.target.value)} placeholder="user@…" className="rounded-none w-52" data-testid="audit-user-filter" />
          </div>
          <div>
            <Label className="overline">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="rounded-none w-32" data-testid="audit-method-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PATCH">PATCH</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
                <SelectItem value="DELETE">DELETE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-none w-40" data-testid="audit-from" />
          </div>
          <div>
            <Label className="overline">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-none w-40" data-testid="audit-to" />
          </div>
          <Button variant="outline" onClick={load} className="rounded-none border-slate-900 h-9" data-testid="audit-refresh">
            <ArrowClockwiseIcon size={14} className="mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="text-sm text-slate-500" data-testid="audit-total">
        Showing latest {items.length} of {total} events
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="uppercase text-xs tracking-wider">When</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">User</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Role</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Method</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Path</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">IP</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => {
              const dt = new Date(it.created_at);
              const when = isNaN(dt) ? it.created_at : dt.toLocaleString();
              return (
                <TableRow key={it.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`audit-row-${it.id}`}>
                  <TableCell className="font-mono-num text-xs whitespace-nowrap">{when}</TableCell>
                  <TableCell className="text-slate-700 font-mono-num text-xs">{it.user_email || "—"}</TableCell>
                  <TableCell className="overline text-slate-500">{it.user_role || "—"}</TableCell>
                  <TableCell className={`font-semibold ${METHOD_COLOR[it.method] || "text-slate-700"} text-xs`}>{it.method}</TableCell>
                  <TableCell className="font-mono-num text-xs text-slate-600">{it.path}{it.query ? `?${it.query}` : ""}</TableCell>
                  <TableCell className="font-mono-num text-xs text-slate-500">{it.ip || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num text-xs">{it.status_code}</TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No audit entries.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
